import { Tag } from "antd";
import { Theme } from "api/config/chalice-types";
import cx from "classnames";
import { useState } from "react"; // Import React and useState
import Skeleton from "react-loading-skeleton";

// --- Helper Function for Consistent Tag Colors --- //
const ANT_DESIGN_TAG_COLORS = [
  "magenta",
  "red",
  "volcano",
  "orange",
  "gold",
  "lime",
  "green",
  "cyan",
  "blue",
  "geekblue",
  "purple",
];

const getColorFromString = (inputString: string): string => {
  if (!inputString) {
    return "default"; // Fallback color
  }
  let hash = 0;
  for (let i = 0; i < inputString.length; i++) {
    const char = inputString.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0; // Convert to 32bit integer
  }
  const index = Math.abs(hash) % ANT_DESIGN_TAG_COLORS.length;
  return ANT_DESIGN_TAG_COLORS[index];
};
// --- End Helper Function --- //

// --- Theme Option Label Component --- //
interface ThemeOptionLabelProps {
  theme: Theme;
  businessThemeNames: string[];
}

const ThemeOptionLabel = ({
  theme,
  businessThemeNames,
}: ThemeOptionLabelProps) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const isSelectedByBusiness = businessThemeNames.includes(theme.name);

  return (
    <div className="flex flex-row items-center gap-2 p-1 w-[200px]">
      <div className="relative h-[80px] w-[80px] flex-shrink-0">
        {!isImageLoaded && (
          <Skeleton
            height={80}
            width={80}
            borderRadius={4}
            className="absolute inset-0"
          />
        )}
        <img
          src={theme.image_url}
          className={cx(
            "h-[80px] w-auto flex-shrink-0 border rounded",
            isSelectedByBusiness
              ? "border-2 border-antd-colorPrimary"
              : "border-gray-200",
            // Hide image until loaded
            { "opacity-0": !isImageLoaded, "opacity-100": isImageLoaded }
          )}
          alt={theme.display_name || theme.name}
          loading="lazy"
          onLoad={() => setIsImageLoaded(true)}
          onError={() => setIsImageLoaded(true)} // Hide skeleton even if image fails
        />
      </div>
      {/* Container for text/tags to the right */}
      <div className="flex flex-col items-start text-left gap-1 overflow-hidden">
        {theme.category && (
          <Tag
            color={getColorFromString(theme.category)}
            className="text-xs m-0"
          >
            {theme.category}
          </Tag>
        )}
        {/* Map over styles to create individual tags */}
        <div className="flex flex-wrap gap-1">
          {theme.style?.map((styleName) => (
            <Tag
              key={styleName}
              color={getColorFromString(styleName)}
              className="text-xs m-0"
            >
              {styleName}
            </Tag>
          ))}
        </div>
      </div>
    </div>
  );
};
// --- End Theme Option Label Component --- //

export default ThemeOptionLabel;
